// Раздел «Роль» карточки пользователя (040: T034; FR-003, FR-004; spec, сценарий 3; contracts/ui.md
// 040, «Пользователи»). Роль на установку одна: у сотрудника — «Сотрудник» и «Сделать
// администратором», у администратора — «Администратор» и «Снять роль администратора», у внешнего —
// «Внешний пользователь» без кнопок. Выбора ролей и областей нет. Сопровождение поставщика —
// строкой, назначается и снимается из «…» карточки (useSupportRole): роль поставщика, не клиента
// (plan, «Решения при планировании»). Владение протоколами — свой раздел карточки
// (OwnedProtocols.tsx), в «Роли» его нет.
//
// Обе смены роли — через окно подтверждения с последствиями (ux.md, раздел 1: затрагивает других
// людей): «Увидит все протоколы, пользователей и настройки»; «Сеансы пользователя завершатся» (039).
// После — тост с итогом без «Отменить»: отмена назначения завершила бы сеансы, а завершённые
// снятием сеансы не вернуть. Снятие идёт с версией назначения, которую видел пользователь
// (If-Match, T144). Последний администратор — отказ сервера ACC-004 тостом с кодом, окно
// закрывается (повторять бессмысленно).
//
// 041 (FR-011, FR-013, FR-014; contracts/ui.md 041, «Пользователи → карточка»): областей
// и замещений нет. Роль со сроком — та, в которую при обновлении перевели замещение (research Р5):
// у неё приглушённо «по ДД.ММ.ГГГГ». Срок вручную не задаётся (research Р6).
import { roleInfo } from '@kp/contracts';
import type { Assignment, UserDetail, UserKind } from '@kp/contracts';
import { Button, ConfirmDialog, useToast } from '@kp/ui';
import { LifeBuoy, ShieldCheck, ShieldOff } from '@kp/ui/icons';
import { useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { api, isApiError } from '../../api/client.ts';
import { useErrorToast } from '../../app/errors.ts';
import { formatDate } from '../../app/format.ts';
import { useRefreshUser } from './user-actions.tsx';

/** Срок роли (041, research Р5): «по 31.10.2026»; без срока — пусто. */
export function termText(assignment: Pick<Assignment, 'validTo'>): string {
  return assignment.validTo ? `по ${formatDate(assignment.validTo)}` : '';
}

/** Своё действующее назначение роли «Администратор» или сопровождения; нет — undefined. */
export function ownAssignment(
  detail: Pick<UserDetail, 'assignments'>,
  role: 'client_admin' | 'support',
): Assignment | undefined {
  return detail.assignments.find((item) => item.role === role && !item.archivedAt);
}

/**
 * Вид пользователя в карточке — как колонка «Роль» списка (UserListItem.kind): внешний — по
 * домену почты (022), администратор и сопровождение — по своему назначению, иначе сотрудник.
 */
export function userKindOf(detail: Pick<UserDetail, 'assignments' | 'external'>): UserKind {
  if (detail.external) return 'external';
  if (ownAssignment(detail, 'client_admin')) return 'admin';
  if (ownAssignment(detail, 'support')) return 'support';
  return 'member';
}

/** Строка роли в карточке: у внешнего — полное «Внешний пользователь» (contracts/ui.md 040). */
export const userKindCardTitles = {
  admin: roleInfo.client_admin.title,
  member: roleInfo.member.title,
  external: 'Внешний пользователь',
  support: roleInfo.support.title,
} as const satisfies Record<UserKind, string>;

/** Последствия «Сделать администратором» (contracts/ui.md 040). */
export const MAKE_ADMIN_TEXT = 'Увидит все протоколы, пользователей и настройки';
export const SESSIONS_END_TEXT = 'Сеансы пользователя завершатся';

/** Окно роли в разделе: назначить или снять «Администратора» (сопровождение — useSupportRole). */
type RoleDialog = 'makeAdmin' | 'removeAdmin';

/**
 * Смена роли из карточки: окна подтверждения и запросы. Отказ, который повтором не исправить
 * (ACC-004 — последний администратор, ACC-007 — внешний, REC-001 — назначение уже изменили,
 * REC-009 — пользователь в архиве), закрывает окно и показывает тост с кодом; остальное — тост
 * с «Повторить», окно остаётся.
 */
function useRoleActions(detail: UserDetail, close: () => void) {
  const toast = useToast();
  const showError = useErrorToast();
  const refresh = useRefreshUser();

  const fail = async (error: unknown, retry: () => void): Promise<never> => {
    if (
      isApiError(error, 'ACC-004') ||
      isApiError(error, 'ACC-007') ||
      isApiError(error, 'REC-001') ||
      isApiError(error, 'REC-009')
    ) {
      close();
      showError(error);
      await refresh();
    } else {
      showError(error, retry);
    }
    throw error;
  };

  const assign = async (role: 'client_admin' | 'support'): Promise<void> => {
    try {
      await api('assignments.create', { body: { userId: detail.id, role } });
    } catch (error) {
      await fail(error, () => void assign(role).catch(() => undefined));
    }
    await refresh();
    if (role === 'client_admin') toast.ok(`${detail.fullName} теперь администратор`);
  };

  const remove = async (assignment: Assignment): Promise<void> => {
    try {
      await api('assignments.archive', {
        params: { id: assignment.id },
        version: assignment.version,
      });
    } catch (error) {
      await fail(error, () => void remove(assignment).catch(() => undefined));
    }
    await refresh();
    toast.ok(
      assignment.role === 'client_admin'
        ? `${detail.fullName} теперь сотрудник`
        : `Сопровождение поставщика снято: ${detail.fullName}`,
    );
  };

  return { assign, remove };
}

/**
 * «…» карточки → сопровождение поставщика: назначается сразу (итог виден в разделе «Роль»),
 * снимается через окно — сеансы пользователя завершатся (039).
 */
export function useSupportRole(
  detail: UserDetail,
  returnFocusRef?: RefObject<HTMLElement | null>,
): {
  assigned: boolean;
  toggle: () => void;
  dialog: ReactNode;
} {
  const [open, setOpen] = useState(false);
  const { assign, remove } = useRoleActions(detail, () => setOpen(false));
  const support = ownAssignment(detail, 'support');
  return {
    assigned: support !== undefined,
    toggle: () => {
      if (support) setOpen(true);
      else void assign('support').catch(() => undefined);
    },
    dialog: (
      <ConfirmDialog
        open={open && support !== undefined}
        onOpenChange={setOpen}
        tone="warn"
        icon={LifeBuoy}
        title={`Снять сопровождение поставщика: ${detail.fullName}?`}
        description={SESSIONS_END_TEXT}
        confirmLabel="Снять сопровождение"
        onConfirm={() => (support ? remove(support) : undefined)}
        returnFocusRef={returnFocusRef}
      />
    ),
  };
}

export function UserRoles({
  detail,
  editable,
}: {
  detail: UserDetail;
  /** Право назначать роли (assignments.manage) и пользователь не в архиве. */
  editable: boolean;
}) {
  const [dialog, setDialog] = useState<RoleDialog | null>(null);
  /**
   * Кнопка роли — одна и та же на месте: после «Сделать администратором» она становится «Снять
   * роль администратора», и фокус из окна возвращается на неё (ux.md, «Слои и Esc»).
   */
  const actionRef = useRef<HTMLButtonElement>(null);
  const { assign, remove } = useRoleActions(detail, () => setDialog(null));

  const kind = userKindOf(detail);
  const admin = ownAssignment(detail, 'client_admin');
  const support = ownAssignment(detail, 'support');

  const makeAdmin = kind === 'member';
  const action =
    editable && (makeAdmin || kind === 'admin') ? (
      <Button
        ref={actionRef}
        size="sm"
        icon={makeAdmin ? ShieldCheck : ShieldOff}
        onClick={() => setDialog(makeAdmin ? 'makeAdmin' : 'removeAdmin')}
      >
        {makeAdmin ? 'Сделать администратором' : 'Снять роль администратора'}
      </Button>
    ) : null;

  return (
    <div className="kp-roles">
      <ul className="kp-list" aria-label="Роль пользователя">
        <li className="kp-list-row kp-role-row">
          <span className="kp-role-title">{userKindCardTitles[kind]}</span>
          <RoleTerm
            assignment={kind === 'admin' ? admin : kind === 'support' ? support : undefined}
          />
          <span className="sp" />
          {action}
        </li>
        {/* Сопровождение у администратора — второй строкой; снимается из «…» карточки. */}
        {support && kind !== 'support' ? (
          <li className="kp-list-row kp-role-row">
            <span className="kp-role-title">{roleInfo.support.title}</span>
            <RoleTerm assignment={support} />
          </li>
        ) : null}
      </ul>
      <ConfirmDialog
        open={dialog === 'makeAdmin'}
        onOpenChange={(open) => !open && setDialog(null)}
        tone="warn"
        icon={ShieldCheck}
        title={`Сделать администратором: ${detail.fullName}?`}
        description={MAKE_ADMIN_TEXT}
        confirmLabel="Сделать администратором"
        onConfirm={() => assign('client_admin')}
        returnFocusRef={actionRef}
      />
      <ConfirmDialog
        open={dialog === 'removeAdmin' && admin !== undefined}
        onOpenChange={(open) => !open && setDialog(null)}
        tone="warn"
        icon={ShieldOff}
        title={`Снять роль администратора: ${detail.fullName}?`}
        description={SESSIONS_END_TEXT}
        confirmLabel="Снять роль администратора"
        onConfirm={() => (admin ? remove(admin) : undefined)}
        returnFocusRef={actionRef}
      />
    </div>
  );
}

/** Срок роли приглушённо после названия: «по 31.10.2026» (041, research Р5). */
function RoleTerm({ assignment }: { assignment: Assignment | undefined }) {
  const term = assignment ? termText(assignment) : '';
  return term ? <span className="t-muted t-num">{term}</span> : null;
}
